import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import './Customers.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface Customer {
  id: number;
  customer_id: string;
  company_name: string;
  contact_name: string;
  email: string;
  phone: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  postal_code: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

const Customers: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters & Search
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  
  // Form data
  const [formData, setFormData] = useState({
    customer_id: '',
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    postal_code: '',
    status: 'active'
  });

// Calculate how many rows fit in viewport
  const calculateFitWindow = () => {
    const windowHeight = window.innerHeight;
    const navHeight = 80;           // Top navigation bar
    const headerHeight = 90;        // Search + filters
    const statsHeight = 60;         // Stats bar
    const tableHeaderHeight = 55;   // Table header
    const paginationHeight = 100;   // Pagination controls
    const padding = 80;             // Extra padding/margins
    const rowHeight = 55;           // Each table row
    
    const availableHeight = windowHeight - navHeight - headerHeight - statsHeight - tableHeaderHeight - paginationHeight - padding;
    const rowsPerPage = Math.floor(availableHeight / rowHeight);
    return Math.max(5, Math.min(rowsPerPage, 100)); // Between 5 and 100
  };

  useEffect(() => {
    loadCustomers();
  }, [search, statusFilter, sortBy, sortOrder, page, limit]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        skip: (page * limit).toString(),
        limit: limit.toString(),
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/customers?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCustomers(response.data.customers);
      setTotal(response.data.total);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const openAddModal = () => {
    setModalMode('add');
    setFormData({
      customer_id: '',
      company_name: '',
      contact_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      country: '',
      postal_code: '',
      status: 'active'
    });
    setShowModal(true);
  };

  const openEditModal = (customer: Customer) => {
    setModalMode('edit');
    setSelectedCustomer(customer);
    setFormData({
      customer_id: customer.customer_id,
      company_name: customer.company_name,
      contact_name: customer.contact_name,
      email: customer.email,
      phone: customer.phone || '',
      address: customer.address || '',
      city: customer.city || '',
      country: customer.country || '',
      postal_code: customer.postal_code || '',
      status: customer.status
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (modalMode === 'add') {
        await axios.post(`${API_URL}/customers`, formData, config);
      } else if (selectedCustomer) {
        const updateData = { ...formData };
        delete (updateData as any).customer_id;
        await axios.put(`${API_URL}/customers/${selectedCustomer.id}`, updateData, config);
      }
      
      setShowModal(false);
      loadCustomers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save customer');
    }
  };

  const handleDelete = async (customer: Customer) => {
    if (!window.confirm(`Delete customer ${customer.company_name}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/customers/${customer.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadCustomers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete customer');
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="customers-page">
      <nav className="customers-nav">
        <h1>📊 Customer Management</h1>
        <div>
          <button onClick={() => navigate('/dashboard')}>← Dashboard</button>
          <span>{user?.email}</span>
          <button onClick={logout}>Logout</button>
        </div>
      </nav>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="customers-container">
        <div className="customers-header">
          <div className="search-box">
            <input
              type="text"
              placeholder="🔍 Search customers..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            />
          </div>
          
          <div className="filters">
            <select 
              value={statusFilter} 
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          
          <button className="btn-primary" onClick={openAddModal}>
            ➕ Add Customer
          </button>
        </div>

        <div className="stats-bar">
          <span>Total: <strong>{total}</strong> customers</span>
          <span>Showing: <strong>{customers.length}</strong></span>
        </div>

        {loading ? (
          <div className="loading">Loading customers...</div>
        ) : (
          <>
            <div className="table-container">
              <table className="customers-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('customer_id')}>
                      ID {sortBy === 'customer_id' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('company_name')}>
                      Company {sortBy === 'company_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('contact_name')}>
                      Contact {sortBy === 'contact_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('email')}>
                      Email {sortBy === 'email' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Phone</th>
                    <th onClick={() => handleSort('city')}>
                      City {sortBy === 'city' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('country')}>
                      Country {sortBy === 'country' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('status')}>
                      Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map(customer => (
                    <tr key={customer.id}>
                      <td><code>{customer.customer_id}</code></td>
                      <td><strong>{customer.company_name}</strong></td>
                      <td>{customer.contact_name}</td>
                      <td>{customer.email}</td>
                      <td>{customer.phone || '-'}</td>
                      <td>{customer.city || '-'}</td>
                      <td>{customer.country || '-'}</td>
                      <td>
                        <span className={`status-badge ${customer.status}`}>
                          {customer.status}
                        </span>
                      </td>
                      <td className="actions">
                        <button onClick={() => openEditModal(customer)} className="btn-edit">
                          ✏️
                        </button>
                        <button onClick={() => handleDelete(customer)} className="btn-delete">
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                  {customers.length === 0 && (
                    <tr>
                      <td colSpan={9} style={{ textAlign: 'center', padding: '40px' }}>
                        No customers found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {total > 0 && (
              <div className="pagination">
                <div className="page-size-selector">
                  <label>Show:</label>
                  <select value={limit} onChange={(e) => {
                    const value = Number(e.target.value);
                    setLimit(value);
                    setPage(0);
                  }}>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={500}>500</option>
                    <option value={calculateFitWindow()}>Fit Window ({calculateFitWindow()})</option>
                    <option value={total}>All ({total})</option>
                  </select>
                  <span>per page</span>
                </div>

                {totalPages > 1 && (
                  <div className="page-controls">
                    <button 
                      onClick={() => setPage(0)} 
                      disabled={page === 0}
                      title="First page"
                    >
                      ⏮
                    </button>
                    <button 
                      onClick={() => setPage(p => Math.max(0, p - 1))} 
                      disabled={page === 0}
                      title="Previous page"
                    >
                      ←
                    </button>
                    <span className="page-info">
                      Page <strong>{page + 1}</strong> of <strong>{totalPages}</strong>
                    </span>
                    <button 
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} 
                      disabled={page >= totalPages - 1}
                      title="Next page"
                    >
                      →
                    </button>
                    <button 
                      onClick={() => setPage(totalPages - 1)} 
                      disabled={page >= totalPages - 1}
                      title="Last page"
                    >
                      ⏭
                    </button>
                  </div>
                )}

                <div className="pagination-info">
                  Showing <strong>{page * limit + 1}</strong> to <strong>{Math.min((page + 1) * limit, total)}</strong> of <strong>{total}</strong>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{modalMode === 'add' ? '➕ Add New Customer' : '✏️ Edit Customer'}</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Customer ID *</label>
                  <input
                    type="text"
                    value={formData.customer_id}
                    onChange={(e) => setFormData({...formData, customer_id: e.target.value})}
                    required
                    disabled={modalMode === 'edit'}
                  />
                </div>
                <div className="form-group">
                  <label>Status *</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Contact Name *</label>
                  <input
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Postal Code</label>
                  <input
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({...formData, country: e.target.value})}
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-cancel">
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {modalMode === 'add' ? 'Create Customer' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
